<template>
  <div class="pdp-feedback-wrapper">
    <v-data-table
      :headers="headers"
      :items="flatParticipantRows"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-feedback-table"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr
            v-for="row in items"
            :key="row.rowKey"
            class="participant-row"
          >
              <td
                v-if="row.isFirstParticipant"
                :rowspan="row.participantCount"
                class="date-time-td"
              >
                <div class="date-time-cell">
                  <div class="date-time-content">
                    <div class="status-label">{{ getMeetingStatus(row.meeting.End_Time || row.meeting.Start_Time) }}</div>
                    <div class="date-text">{{ formatDateTime(row.meeting.Start_Time) }}</div>
                  </div>
                  <v-btn
                    v-if="isHRorFinancierOrManager"
                    x-small
                    icon
                    class="delete-meeting-btn"
                    @click.stop="deleteMeeting(row.meeting)"
                  >
                    <v-icon x-small color="error">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </td>

              <td class="participant-td">
                <div class="participant-name-cell">
                  <span>{{ row.participant.Full_Name }}</span>
                  <div class="participant-actions">
                    <v-icon
                      v-if="canEditParticipant(row.participant, row.meeting) && row.participant.Employee_ID !== employee.Employee_ID"
                      small
                      class="action-icon action-icon--delete error--text"
                      @click="removeParticipant(row.participant, row.meeting)"
                    >
                      mdi-delete-outline
                    </v-icon>
                    <span
                      v-if="row.isLastParticipant && canEditMeeting(row.meeting) && canEditParticipant(row.participant, row.meeting) && row.participant.Employee_ID !== employee.Employee_ID"
                      class="participant-actions-divider"
                    />
                  <v-btn
                    v-if="row.isLastParticipant && canEditMeeting(row.meeting)"
                    x-small
                    icon
                    class="add-participant-btn"
                    @click="showAddParticipantDialog(row.meeting)"
                  >
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                  </div>
                </div>
              </td>

              <td class="role-td">
                <v-select
                  v-if="canEditParticipant(row.participant, row.meeting)"
                  v-model="row.participant.selectedRole"
                  :items="availableRoles"
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Role"
                  class="role-select"
                  @change="updateParticipantRole(row.participant)"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small class="mr-1" color="primary" text-color="white">{{ item }}</v-chip>
                  </template>
                </v-select>
                <v-select
                  v-else
                  :value="row.participant.selectedRole || row.participant.Roles || ''"
                  :items="row.participant.selectedRole || row.participant.Roles ? [row.participant.selectedRole || row.participant.Roles] : []"
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Role"
                  class="role-select"
                  disabled
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small class="mr-1" color="primary" text-color="white">{{ item }}</v-chip>
                  </template>
                </v-select>
              </td>

              <td class="feedback-td">
                <v-select
                  v-if="canEditParticipant(row.participant, row.meeting)"
                  v-model="row.participant.Feedback"
                  :items="feedbackOptions.filter(f => f !== 'Feedback')"
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Feedback"
                  class="feedback-select"
                  @change="updateParticipantFeedback(row.participant)"
                >
                  <template v-slot:item="{ item }">
                    <v-chip :color="getFeedbackColor(item)" small text-color="white">{{ item }}</v-chip>
                  </template>
                  <template v-slot:selection="{ item }">
                    <v-chip :color="getFeedbackColor(item)" small text-color="white">{{ item }}</v-chip>
                  </template>
                </v-select>
                <v-select
                  v-else
                  :value="row.participant.Feedback || 'Feedback'"
                  :items="row.participant.Feedback ? [row.participant.Feedback] : ['Feedback']"
                  dense
                  flat
                  solo
                  hide-details
                  class="feedback-select"
                  disabled
                >
                  <template v-slot:selection="{ item }">
                    <v-chip v-if="item !== 'Feedback'" :color="getFeedbackColor(item)" small text-color="white">{{ item }}</v-chip>
                    <span v-else class="grey--text caption">{{ item }}</span>
                  </template>
                </v-select>
              </td>

              <td class="notes-td">
                <div class="notes-cell">
                  <div class="notes-content">
                    <div v-if="row.participant.notes && row.participant.notes.length > 0" class="notes-list">
                      <span
                        v-for="note in row.participant.notes"
                        :key="note.Note_ID"
                        class="note-comment"
                        :class="{ 'note-comment--long': isNoteLong(note.Note_Text) }"
                        @click="isNoteLong(note.Note_Text) && openFullNoteModal(note, row.participant)"
                      >
                        {{ truncateNote(note.Note_Text) }}
                        <v-icon
                          v-if="canEditParticipant(row.participant, row.meeting)"
                          x-small
                          class="note-comment__delete"
                          @click.stop="deleteNote(row.participant, note)"
                        >
                          mdi-close
                        </v-icon>
                      </span>
                    </div>
                    <v-textarea
                      v-if="canEditParticipant(row.participant, row.meeting) && addingNoteParticipantId === row.participant.Participant_ID"
                      :ref="'noteInput-' + row.participant.Participant_ID"
                      :value="noteDrafts[row.participant.Participant_ID] || ''"
                      placeholder="Note"
                      rows="1"
                      auto-grow
                      dense
                      flat
                      solo
                      hide-details
                      class="notes-inline-input"
                      @input="setNoteDraft(row.participant.Participant_ID, $event)"
                      @blur="onNoteBlur(row.participant)"
                      @keydown.enter.prevent="onNoteBlur(row.participant)"
                    />
                    <div v-else-if="!row.participant.notes || row.participant.notes.length === 0" class="no-notes">No notes</div>
                  </div>
                  <v-btn
                    v-if="canEditParticipant(row.participant, row.meeting)"
                    x-small
                    icon
                    class="add-note-btn"
                    @click="showNewNoteField(row.participant)"
                  >
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <v-dialog v-model="showFullNoteDialog" max-width="500" @click:outside="showFullNoteDialog = false">
      <v-card>
        <v-card-title class="text-subtitle-1">
          Comment{{ fullNoteAuthor ? ` from ${fullNoteAuthor}` : '' }}
        </v-card-title>
        <v-card-text class="full-note-text">{{ fullNoteText }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showFullNoteDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showParticipantDialog" max-width="600">
      <v-card>
        <v-card-title>Add Participant</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedEmployee"
            :items="availableEmployees"
            item-text="full_name"
            item-value="employee_id"
            label="Select Employee"
            solo
            dense
            :search-input.sync="employeeSearch"
            :loading="loadingEmployees"
          >
            <template v-slot:item="{ item }">
              <div>{{ item.full_name }}</div>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeParticipantDialog">Cancel</v-btn>
          <v-btn color="primary" :disabled="!selectedEmployee" @click="addParticipant">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="showDeleteParticipantDialog"
      v-model="showDeleteParticipantDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="currentParticipantToRemove ? `Are you sure you want to remove ${currentParticipantToRemove.participant.Full_Name} from this meeting?` : ''"
      @onDeleteClick="confirmRemoveParticipant"
      @onCancelClick="showDeleteParticipantDialog = false; currentParticipantToRemove = null"
      @onCloseClick="showDeleteParticipantDialog = false; currentParticipantToRemove = null"
    />

    <ConfirmOrDeniedDialog
      v-if="showDeleteMeetingDialog"
      v-model="showDeleteMeetingDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="currentMeetingToDelete ? `Are you sure you want to delete meeting on ${formatDateTime(currentMeetingToDelete.Start_Time)}?` : ''"
      @onDeleteClick="confirmDeleteMeeting"
      @onCancelClick="showDeleteMeetingDialog = false; currentMeetingToDelete = null"
      @onCloseClick="showDeleteMeetingDialog = false; currentMeetingToDelete = null"
    />
  </div>
</template>

<script>
export default {
  name: 'PdpFeedbackTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      headers: [
        { text: 'Date & Time', value: 'dateTime', sortable: false, width: '100px', align: 'center' },
        { text: 'Participants', value: 'participants', sortable: false, width: '250px', align: 'center' },
        { text: 'Role', value: 'roles', sortable: false, align: 'center' },
        { text: 'Feedback', value: 'feedback', sortable: false, align: 'center' },
        { text: 'Notes', value: 'notes', sortable: false, align: 'center' }
      ],
      meetings: [],
      participants: {},
      currentParticipant: null,
      newNoteText: '',
      noteDrafts: {},
      addingNoteParticipantId: null,
      showParticipantDialog: false,
      currentMeeting: null,
      selectedEmployee: null,
      availableEmployees: [],
      employeeSearch: '',
      loadingEmployees: false,
      showDeleteParticipantDialog: false,
      currentParticipantToRemove: null,
      showDeleteMeetingDialog: false,
      currentMeetingToDelete: null,
      showFullNoteDialog: false,
      fullNoteText: '',
      fullNoteAuthor: '',
      noteTruncateLength: 150,
      availableRoles: [
        'Employee',
        'Supervisor',
        'Project Manager',
        'Tech Lead',
        'Team Lead',
        'Team Member',
        'HR Manager',
        'Account Manager'
      ],
      feedbackOptions: ['Feedback', 'Positive', 'Neutral', 'Negative']
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    meetingsWithParticipants () {
      return this.meetings.map(meeting => {
        const meetingParticipants = this.participants[meeting.Meeting_ID] || []
        return {
          ...meeting,
          participants: meetingParticipants.map(p => {
            return {
              ...p,
              selectedRole: p.Roles || this.getSuggestedRoleForParticipant(p) || null,
              notes: p.notes || []
            }
          })
        }
      })
    },
    flatParticipantRows () {
      const rows = []
      for (const meeting of this.meetingsWithParticipants) {
        const participants = meeting.participants || []
        const participantCount = participants.length || 1
        participants.forEach((participant, index) => {
          rows.push({
            rowKey: `${meeting.Meeting_ID}-${participant.Participant_ID}`,
            meeting,
            participant,
            isFirstParticipant: index === 0,
            isLastParticipant: index === participants.length - 1,
            participantCount
          })
        })
        if (participants.length === 0) {
          rows.push({
            rowKey: `${meeting.Meeting_ID}-empty`,
            meeting,
            participant: { Full_Name: '—', notes: [] },
            isFirstParticipant: true,
            isLastParticipant: true,
            participantCount: 1
          })
        }
      }
      return rows
    }
  },
  async mounted () {
    if (this.employee && this.employee.Employee_ID) {
      await this.loadMeetings()
    }
  },
  methods: {
    canEditParticipant (participant, meeting) {
      if (!participant || !meeting) return false
      const isOwnData = participant.Employee_ID === this.user.employee_id
      return this.isHRorFinancierOrManager || (isOwnData && this.isCurrentEmployee)
    },
    canEditMeeting (meeting) {
      if (!meeting) return false
      return this.isHRorFinancierOrManager
    },
    getMeetingStatus (dateString) {
      if (!dateString) return ''
      const meetingDate = new Date(dateString)
      const now = new Date()
      return meetingDate >= now ? 'Upcoming' : 'Past'
    },
    async loadMeetings () {
      try {
        const response = await this.axios.get(`/api/pdp/meetings/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.meetings = response.data

        for (const meeting of this.meetings) {
          await this.loadParticipants(meeting.Meeting_ID)
        }
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    getSuggestedRoleForParticipant (participant) {
      if (!participant || !this.employee) return null
      const empId = participant.Employee_ID
      if (empId === this.employee.Employee_ID) return 'Employee'
      if (this.employee.Supervisor_ID && empId === this.employee.Supervisor_ID) return 'Supervisor'
      return null
    },
    async loadParticipants (meetingId) {
      try {
        const response = await this.axios.get(`/api/pdp/meetings/${meetingId}/participants`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const participants = response.data

        for (const participant of participants) {
          await this.loadParticipantNotes(participant)
        }

        this.$set(this.participants, meetingId, participants)
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async loadParticipantNotes (participant) {
      try {
        const response = await this.axios.get(`/api/pdp/participants/${participant.Participant_ID}/notes`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$set(participant, 'notes', response.data)
      } catch (error) {
        this.$set(participant, 'notes', [])
      }
    },
    async updateParticipantRole (participant) {
      try {
        await this.axios.put(`/api/pdp/participants/${participant.Participant_ID}/roles`, {
          roles: participant.selectedRole || null
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        participant.Roles = participant.selectedRole || null
        this.$store.dispatch('setSuccess', { successMsg: 'Role updated successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async updateParticipantFeedback (participant) {
      try {
        await this.axios.put(`/api/pdp/participants/${participant.Participant_ID}/feedback`, {
          feedback: participant.Feedback
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$store.dispatch('setSuccess', { successMsg: 'Feedback updated successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    setNoteDraft (participantId, value) {
      this.$set(this.noteDrafts, participantId, value)
    },
    showNewNoteField (participant) {
      this.addingNoteParticipantId = participant.Participant_ID
      this.$set(this.noteDrafts, participant.Participant_ID, '')
      this.$nextTick(() => {
        const ref = this.$refs['noteInput-' + participant.Participant_ID]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.$el) {
          const textarea = comp.$el.querySelector('textarea')
          if (textarea) textarea.focus()
        }
      })
    },
    onNoteBlur (participant) {
      const text = (this.noteDrafts[participant.Participant_ID] || '').trim()
      this.addingNoteParticipantId = null
      if (!text) {
        this.$set(this.noteDrafts, participant.Participant_ID, '')
        return
      }
      this.currentParticipant = participant
      this.newNoteText = text
      this.addNote()
      this.$set(this.noteDrafts, participant.Participant_ID, '')
    },
    async addNote () {
      if (!this.newNoteText.trim() || !this.currentParticipant) return

      try {
        await this.axios.post(`/api/pdp/participants/${this.currentParticipant.Participant_ID}/notes`, {
          noteText: this.newNoteText
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipantNotes(this.currentParticipant)
        this.newNoteText = ''
        this.$store.dispatch('setSuccess', { successMsg: 'Note added successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async deleteNote (participant, note) {
      try {
        await this.axios.delete(`/api/pdp/participants/notes/${note.Note_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipantNotes(participant)
        this.$store.dispatch('setSuccess', { successMsg: 'Note deleted successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async showAddParticipantDialog (meeting) {
      this.currentMeeting = meeting
      this.selectedEmployee = null
      this.employeeSearch = ''
      await this.loadEmployees()
      this.showParticipantDialog = true
    },
    closeParticipantDialog () {
      this.showParticipantDialog = false
      this.currentMeeting = null
      this.selectedEmployee = null
      this.employeeSearch = ''
      this.availableEmployees = []
    },
    async loadEmployees () {
      try {
        this.loadingEmployees = true
        const response = await this.axios.get('/api/employeelist', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const existingParticipantIds = new Set(
          (this.currentMeeting.participants || []).map(p => p.Employee_ID)
        )
        const allEmployees = Array.isArray(response.data) ? response.data : []

        const isCurrentEmployeeParticipant = existingParticipantIds.has(this.employee.Employee_ID)

        const availableEmployees = allEmployees.filter(emp =>
          emp.employee_id && !existingParticipantIds.has(emp.employee_id)
        )

        if (!isCurrentEmployeeParticipant && this.employee.Employee_ID) {
          const currentEmployeeInList = allEmployees.find(emp => emp.employee_id === this.employee.Employee_ID)
          if (!currentEmployeeInList) {
            availableEmployees.push({
              employee_id: this.employee.Employee_ID,
              full_name: this.employee.Full_Name || this.employee.Employee_Name,
              login: this.employee.Login || ''
            })
          } else {
            availableEmployees.push(currentEmployeeInList)
          }
        }

        this.availableEmployees = availableEmployees
      } catch (error) {
        console.error('Error loading employees:', error)
        this.$store.dispatch('setError', { errorMsg: 'Error loading employees: ' + (error.message || 'Unknown error') })
        this.availableEmployees = []
      } finally {
        this.loadingEmployees = false
      }
    },
    async addParticipant () {
      if (!this.selectedEmployee || !this.currentMeeting) return

      try {
        await this.axios.post(`/api/pdp/meetings/${this.currentMeeting.Meeting_ID}/participants`, {
          employeeId: this.selectedEmployee
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipants(this.currentMeeting.Meeting_ID)
        this.closeParticipantDialog()
        this.$store.dispatch('setSuccess', { successMsg: 'Participant added successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    removeParticipant (participant, meeting) {
      this.currentParticipantToRemove = { participant, meeting }
      this.showDeleteParticipantDialog = true
    },
    async confirmRemoveParticipant () {
      if (!this.currentParticipantToRemove) return

      const { participant, meeting } = this.currentParticipantToRemove
      try {
        await this.axios.delete(`/api/pdp/participants/${participant.Participant_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipants(meeting.Meeting_ID)
        this.showDeleteParticipantDialog = false
        this.currentParticipantToRemove = null
        this.$store.dispatch('setSuccess', { successMsg: 'Participant removed successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    formatDateTime (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    },
    getFeedbackColor (feedback) {
      if (!feedback || feedback === 'Feedback') return 'grey'
      const colors = {
        Positive: 'green',
        Neutral: 'orange',
        Negative: 'red'
      }
      return colors[feedback] || 'grey'
    },
    truncateNote (text) {
      if (!text || typeof text !== 'string') return ''
      if (text.length <= this.noteTruncateLength) return text
      return text.slice(0, this.noteTruncateLength).trim() + '...'
    },
    isNoteLong (text) {
      return text && typeof text === 'string' && text.length > this.noteTruncateLength
    },
    openFullNoteModal (note, participant) {
      this.fullNoteText = note.Note_Text || ''
      this.fullNoteAuthor = (participant && participant.Full_Name) || ''
      this.showFullNoteDialog = true
    },
    deleteMeeting (meeting) {
      this.currentMeetingToDelete = meeting
      this.showDeleteMeetingDialog = true
    },
    async confirmDeleteMeeting () {
      if (!this.currentMeetingToDelete) return
      const meetingId = this.currentMeetingToDelete.Meeting_ID
      try {
        await this.axios.delete(`/api/pdp/meetings/${meetingId}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.meetings = this.meetings.filter(m => m.Meeting_ID !== meetingId)
        this.$delete(this.participants, meetingId)
        this.$store.dispatch('setSuccess', { successMsg: 'Meeting deleted successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      } finally {
        this.showDeleteMeetingDialog = false
        this.currentMeetingToDelete = null
      }
    }
  }
}
</script>

<style scoped>
.pdp-feedback-wrapper {
  padding: 16px;
}

.pdp-feedback-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.pdp-feedback-table >>> .v-data-table__wrapper {
  border: none;
  border-radius: 4px;
  overflow-x: auto;
}

.pdp-feedback-table >>> table {
  border-collapse: collapse;
}

.pdp-feedback-table >>> table tbody td {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  padding: 4px !important;
}

.theme--dark .pdp-feedback-table >>> table tbody td {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.pdp-feedback-table >>> table thead th {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .pdp-feedback-table >>> table thead th {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.participant-row td {
  height: 48px;
}

.pdp-feedback-table >>> tbody tr:hover {
  background: transparent !important;
}

.pdp-feedback-table >>> tbody tr:hover td {
  background-color: #eeeeee !important;
}

.theme--dark .pdp-feedback-table >>> tbody tr:hover td {
  background-color: #616161 !important;
}

.pdp-feedback-table >>> tbody tr:hover td.date-time-td {
  background: transparent !important;
}

.pdp-feedback-table >>> td.date-time-td {
  padding: 8px !important;
}

.date-time-td {
  vertical-align: middle !important;
}

.date-time-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
  gap: 4px;
}

.delete-meeting-btn {
  flex-shrink: 0;
  opacity: 0.6;
}

.delete-meeting-btn:hover {
  opacity: 1;
}

.date-time-content {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .status-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.date-text {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
}

.action-icons {
  display: flex;
  align-items: center;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.participant-name-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.participant-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.participant-actions-divider {
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.theme--dark .participant-actions-divider {
  background: rgba(255, 255, 255, 0.3);
}

.notes-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notes-content {
  flex: 1;
  min-width: 0;
}

.role-select,
.feedback-select {
  font-size: 0.875rem;
}

.role-select,
.feedback-select {
  width: 100%;
}

.role-select >>> .v-input__control,
.feedback-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.role-select >>> .v-input__slot,
.feedback-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 2px !important;
  min-height: 24px !important;
  align-items: center !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.role-select >>> .v-input__slot::before,
.role-select >>> .v-input__slot::after,
.feedback-select >>> .v-input__slot::before,
.feedback-select >>> .v-input__slot::after {
  display: none !important;
}

.role-select >>> .v-input,
.feedback-select >>> .v-input {
  padding-top: 0 !important;
  margin-top: 0 !important;
  width: fit-content !important;
  min-width: 0;
  margin-left: auto !important;
  margin-right: auto !important;
}

.role-select >>> .v-input__slot,
.role-select >>> .v-select__selections,
.feedback-select >>> .v-input__slot,
.feedback-select >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

/* Прибираємо пустий input у v-select, щоб чіп можна було відцентрувати */
.role-select >>> .v-select__selections input,
.feedback-select >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.role-select >>> .v-input__append-inner,
.feedback-select >>> .v-input__append-inner {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: center !important;
}

.role-select >>> .v-select__selection,
.feedback-select >>> .v-select__selection {
  overflow: hidden;
  max-width: calc(100% - 24px);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.role-select >>> .v-select__selections,
.feedback-select >>> .v-select__selections {
  padding-left: 0;
}

.role-select >>> .v-select__selections .v-select__selection--comma,
.feedback-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.role-select >>> .v-select__selection .v-chip,
.feedback-select >>> .v-select__selection .v-chip {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-by-author {
  margin-bottom: 2px;
}

.notes-author-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.7);
  margin-bottom: 4px;
}

.theme--dark .notes-author-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.8);
}

.notes-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.note-comment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete {
  cursor: pointer;
  opacity: 0.6;
}

.note-comment__delete:hover {
  opacity: 1;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.no-notes {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.5);
  margin-bottom: 4px;
}

.theme--dark .no-notes {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.6);
}

.add-note-btn,
.add-participant-btn {
  flex-shrink: 0;
}

.notes-inline-input {
  margin-top: 4px;
  font-size: 0.875rem;
}

.notes-inline-input >>> .v-input__control {
  min-height: 28px !important;
}

.notes-inline-input >>> .v-input__slot {
  padding: 0 4px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.notes-inline-input >>> .v-input__slot::before,
.notes-inline-input >>> .v-input__slot::after {
  display: none !important;
}
</style>
